/**
 * Utility functions
 */

import { clsx, type ClassValue } from 'clsx';

/**
 * Merge class names with clsx
 */
export function cn(...inputs: ClassValue[]) {
  return clsx(inputs);
}

/**
 * Format date for display
 */
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format date relative (e.g., "2 days ago")
 */
export function formatRelativeDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - date.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
}

/**
 * Truncate text to a maximum length
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trim() + '...';
}

/**
 * Build an SEO meta description in the ~150–160 char target range.
 *
 * Search engines flag descriptions that are too short and truncate ones past
 * ~160 chars, so we lead with the hand-written hook (`lead` — an article
 * subtitle or pillar dek) and extend with supporting prose (`body` — an
 * executive summary or CTA body) until we land near `max`, ending on a
 * sentence boundary where one falls in range and otherwise on a word boundary
 * with an ellipsis (never mid-word). Only the description string is produced —
 * it never touches canonical/robots/title.
 */
export function buildMetaDescription(
  lead: string | null | undefined,
  body: string | null | undefined,
  max = 158,
  min = 110,
): string {
  const clean = (s: string | null | undefined) => (s ?? '').replace(/\s+/g, ' ').trim();
  const head = clean(lead);
  const tail = clean(body);

  let text = head;
  if (head.length < max && tail) {
    const sep = head ? (/[.!?]$/.test(head) ? ' ' : '. ') : '';
    text = clean(head + sep + tail);
  }
  if (text.length <= max) return text;

  const window = text.slice(0, max);
  // Prefer ending on a sentence boundary, but only when it lands near the
  // target — an early boundary wastes headroom, so fall through to a fuller
  // word-boundary cut instead of emitting a too-short description.
  const sentenceEnd = Math.max(
    window.lastIndexOf('. '),
    window.lastIndexOf('! '),
    window.lastIndexOf('? '),
  );
  if (sentenceEnd >= Math.max(0, max - 30)) return window.slice(0, sentenceEnd + 1).trim();

  // Otherwise cut at the last word boundary and add an ellipsis. Reserve one
  // char for the ellipsis so the result never exceeds `max`, even when the
  // window has no usable space (a very long word or URL).
  const lastSpace = window.lastIndexOf(' ');
  const cut = (lastSpace >= min ? window.slice(0, lastSpace) : window.slice(0, max - 1))
    .replace(/[\s.,;:!?–—-]+$/, '')
    .trim();
  return `${cut}…`;
}

/**
 * Convert markdown-style bold to HTML
 */
export function markdownBoldToHtml(text: string): string {
  return text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
}

/**
 * Strip markdown formatting
 */
export function stripMarkdown(text: string): string {
  return text
    .replace(/\*\*([^*]+)\*\*/g, '$1') // Bold
    .replace(/\*([^*]+)\*/g, '$1') // Italic
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Links
    .replace(/`([^`]+)`/g, '$1'); // Code
}

/**
 * Debounce function for search input
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Format seconds as "M:SS" for audio clocks / queue runtimes.
 * Returns "0:00" for invalid / NaN / negative inputs.
 */
export function formatClock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * Format a minute count compactly: "12 min", "1 min".
 * Rounds up — useful for "reading time" / "queue total".
 */
export function formatMinutes(seconds: number | undefined | null): string {
  // Intentionally accepts 0 — a 0-second track still formats as "1 min"
  // (the Math.max floor). Treat only nullish / non-finite as absent.
  if (seconds == null || !Number.isFinite(seconds)) return '';
  return `${Math.max(1, Math.ceil(seconds / 60))} min`;
}

/**
 * Health goal ID to display name mapping
 */
export const HEALTH_GOAL_NAMES: Record<string, string> = {
  'heart-health': 'Heart Health',
  'cardiovascular': 'Cardiovascular & Circulation',
  'metabolic': 'Metabolic Health',
  'blood-sugar': 'Blood Sugar Control',
  'brain-function': 'Brain & Cognitive Function',
  'brain-mood': 'Brain, Mood & Cognitive Performance',
  'sleep': 'Sleep & Recovery',
  'immune': 'Immune System',
  'inflammation': 'Immune System Health & Inflammation',
  'energy': 'Energy & Vitality',
  'bone': 'Bone Health',
  'hormones': 'Hormone Balance',
  'stress': 'Stress & Cortisol',
  'athletic': 'Athletic Performance',
  'longevity': 'Longevity & Aging',
};

/**
 * Get display name for health goal
 */
export function getHealthGoalName(id: string): string {
  return HEALTH_GOAL_NAMES[id] || id
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Health goal icons
 */
export const HEALTH_GOAL_ICONS: Record<string, string> = {
  'heart-health': '❤️',
  'cardiovascular': '❤️',
  'metabolic': '⚡',
  'blood-sugar': '🩸',
  'brain-function': '🧠',
  'brain-mood': '🧠',
  'sleep': '😴',
  'immune': '🛡️',
  'inflammation': '🔥',
  'energy': '⚡',
  'bone': '🦴',
  'hormones': '⚖️',
  'stress': '🧘',
  'athletic': '💪',
  'longevity': '🌿',
};

/**
 * Get icon for health goal
 */
export function getHealthGoalIcon(id: string): string {
  return HEALTH_GOAL_ICONS[id] || '📊';
}

/**
 * Scroll to element with offset for sticky header
 */
export function scrollToElement(elementId: string, offset: number = 80): void {
  const element = document.getElementById(elementId);
  if (element) {
    const top = element.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: 'smooth' });
  }
}

/**
 * Health goal color system
 */
export type GoalColors = { primary: string; light: string; icon: string; label: string };

export const DEFAULT_GOAL_COLOR: GoalColors = {
  primary: '#64748B', light: '#F8FAFC', icon: '📋', label: 'Research'
};

/**
 * TODO(phase-3): once ArticleCard/GoalTile consume GOAL_METADATA from
 * lib/aviado/goals.ts directly, delete this table. Kept through phases 2–6
 * so existing components keep rendering without gray fallback colors.
 *
 * Includes v2.7 canonical slugs + legacy slugs so either id resolves.
 */
export const HEALTH_GOAL_COLORS: Record<string, GoalColors> = {
  // v2.7 canonical
  'cardiovascular':    { primary: '#B91C3C', light: '#FFF1F2', icon: '❤️', label: 'Cardiovascular & Circulation' },
  'metabolic':         { primary: '#B45309', light: '#FFFBEB', icon: '🔥', label: 'Metabolic Health' },
  'brain-cognitive':   { primary: '#4338CA', light: '#E8EDFF', icon: '🧠', label: 'Brain, Mood & Cognitive Performance' },
  'sleep':             { primary: '#3B5998', light: '#F0F4F8', icon: '😴', label: 'Sleep & Recovery' },
  'hormonal':          { primary: '#64748B', light: '#F8FAFC', icon: '⚡', label: 'Hormonal Health' },
  'muscle-bone-joint': { primary: '#0369A1', light: '#F0F9FF', icon: '💪', label: 'Muscle, Bone & Joint' },
  'immune':            { primary: '#0F766E', light: '#F0FDFA', icon: '🛡️', label: 'Immune System Health' },
  'longevity':         { primary: '#166534', light: '#F0FDF4', icon: '🧬', label: 'Cellular Longevity' },
  'gut':               { primary: '#7C2D12', light: '#FEF3C7', icon: '🦠', label: 'Gut & Microbiome' },
  'skin-hair':         { primary: '#A21CAF', light: '#FAE8FF', icon: '✨', label: 'Skin, Hair & Collagen' },
  // Legacy aliases (disk fallback / old DB rows)
  'heart-health':      { primary: '#B91C3C', light: '#FFF1F2', icon: '❤️', label: 'Heart Health' },
  'brain-function':    { primary: '#4338CA', light: '#E8EDFF', icon: '🧠', label: 'Brain Function' },
  'hormones':          { primary: '#64748B', light: '#F8FAFC', icon: '⚡', label: 'Hormones' },
  // Editorial topics (surfaced as goal-like tiles even though they're `topics[]` not goals)
  'stress':            { primary: '#6D28D9', light: '#F5F3FF', icon: '🧘', label: 'Stress & Mood' },
  'athletic':          { primary: '#0369A1', light: '#F0F9FF', icon: '💪', label: 'Athletic Performance' },
};

export function getHealthGoalColors(goalId: string): GoalColors {
  return HEALTH_GOAL_COLORS[goalId] || {
    ...DEFAULT_GOAL_COLOR,
    label: HEALTH_GOAL_NAMES[goalId] || goalId.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
    icon: HEALTH_GOAL_ICONS[goalId] || DEFAULT_GOAL_COLOR.icon,
  };
}
